// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandler,
    createHandlerChainRunner,
    makeHandlerChainAwaitable,
} from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import {
    getPreviouslyPurchased,
    PreviouslyPurchasedApiParameter,
    PreviouslyPurchasedCollectionModel,
} from "@pace/App/Services/PreviouslyPurchasedService";

type HandlerType = ApiHandler<
    PreviouslyPurchasedApiParameter,
    ServiceResult<PreviouslyPurchasedCollectionModel>
>;

export const flattenFormattedParamsToApi = (params: PreviouslyPurchasedApiParameter) => {
    const formattedApiParameters: { [key: string]: any } = {
        page: params.page,
        pageSize: params.pageSize,
        OrderDateSortType: params.sortBy?.orderDateSortType || "desc",
    };
    Object.keys(params.filters).forEach((key: string) => {
        const filterValue = params.filters[key as keyof typeof params.filters];
        if (filterValue !== undefined) {
            formattedApiParameters[`filters.${key}`] = filterValue;
        }
    });
    return formattedApiParameters;
};

export const PopulateApiParameters: HandlerType = props => {
    props.apiParameter = flattenFormattedParamsToApi(props.parameter) as any;
};

export const DispatchBeginLoading: HandlerType = props => {
    props.dispatch({
        type: "Data/PreviouslyPurchased/BeginLoading",
        parameter: props.apiParameter,
    } as any);
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.apiResult = await getPreviouslyPurchased(props.apiParameter as any);
    } catch (error) {
        logger.error(error);
    }
};

export const PopulateRelatedProducts: HandlerType = props => {
    if (!props.apiResult.successful) {
        return;
    }
    const productNumbers = props.apiResult.result.previouslyPurchasedProductList?.map(
        ({ productErpNumber }) => productErpNumber,
    );
    if (productNumbers?.length) {
        props.dispatch({
            type: "Data/Products/CompleteLoadProducts",
            collection: {
                products: props.apiResult.result.productCollectionApiModel?.products,
            } as any,
            parameter: { productNumbers },
        });
    }
};

export const AsyncLoadRealTimeInfo: HandlerType = props => {
    if (!props.apiResult.successful) {
        return;
    }
    const productNumbers = props.apiResult.result.previouslyPurchasedProductList?.map(
        ({ productErpNumber }) => productErpNumber,
    );
    if (productNumbers?.length) {
        const awaitableFetchProductsInfo = makeHandlerChainAwaitable(loadProductInfoList);
        awaitableFetchProductsInfo({
            getProductCollectionParameter: { productNumbers },
            id: getDataViewKey(props.apiParameter),
        })(props.dispatch, props.getState);
    }
};

export const DispatchCompleteLoadPreviouslyPurchased: HandlerType = props => {
    props.dispatch({
        type: "Data/PreviouslyPurchased/CompleteLoading",
        parameter: props.apiParameter,
        collection: props.apiResult.successful ? props.apiResult.result : {},
    } as any);
};

export const chain = [
    DispatchBeginLoading,
    PopulateApiParameters,
    RequestDataFromApi,
    PopulateRelatedProducts,
    AsyncLoadRealTimeInfo,
    DispatchCompleteLoadPreviouslyPurchased,
];

const loadPreviouslyPurchased = createHandlerChainRunner(chain, "loadPreviouslyPurchased");

export default loadPreviouslyPurchased;
